import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Grape, Tractor, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

export default function RegisterPage() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [role, setRole] = useState(searchParams.get("role") === "producer" ? "producer" : "candidate");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError(t("common.errorGeneric"));
      return;
    }
    setLoading(true);
    const result = await register(email, password, role);
    setLoading(false);
    if (result.success) {
      toast.success(t("auth.accountCreatedTitle"));
      navigate(role === "candidate" ? "/onboarding/candidate" : "/dashboard/producer/offers/new");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16 sm:py-24">
      <div className="text-center mb-8">
        <Grape className="h-8 w-8 text-[#4ba3b7] mx-auto mb-3" />
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">{t("auth.registerTitle")}</h1>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <button
          type="button"
          onClick={() => setRole("candidate")}
          data-testid="register-role-candidate-button"
          className={`rounded-xl border p-4 text-center transition-colors ${role === "candidate" ? "border-[#4ba3b7] bg-[#4ba3b7]/10" : "border-black/10 bg-white"}`}
        >
          <User className="h-5 w-5 mx-auto mb-2 text-[#4ba3b7]" />
          <span className="text-sm font-semibold">{t("auth.roleCandidate")}</span>
        </button>
        <button
          type="button"
          onClick={() => setRole("producer")}
          data-testid="register-role-producer-button"
          className={`rounded-xl border p-4 text-center transition-colors ${role === "producer" ? "border-[#4ba3b7] bg-[#4ba3b7]/10" : "border-black/10 bg-white"}`}
        >
          <Tractor className="h-5 w-5 mx-auto mb-2 text-[#4ba3b7]" />
          <span className="text-sm font-semibold">{t("auth.roleProducer")}</span>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 className="h-12 rounded-xl mt-1.5" data-testid="register-email-input" />
        </div>
        <div>
          <Label htmlFor="password">{t("auth.password")}</Label>
          <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                 className="h-12 rounded-xl mt-1.5" data-testid="register-password-input" />
        </div>
        <div>
          <Label htmlFor="confirmPassword">{t("auth.confirmPassword")}</Label>
          <Input id="confirmPassword" type="password" required minLength={6} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                 className="h-12 rounded-xl mt-1.5" data-testid="register-confirm-password-input" />
        </div>
        {error && <p className="text-sm text-[#FF3B30]" data-testid="register-error-message">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] font-bold" data-testid="register-submit-button">
          {loading ? t("common.loading") : t("auth.registerButton")}
        </Button>
      </form>

      <p className="text-center text-sm text-[#2B2B2B]/60 mt-6">
        {t("auth.alreadyHaveAccount")}{" "}
        <Link to="/login" className="text-[#4ba3b7] font-semibold hover:underline" data-testid="register-login-link">
          {t("auth.loginButton")}
        </Link>
      </p>
    </div>
  );
}
